/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

import { css, cva, cx } from "@panda/css";
import { Box, Flex, Stack } from "@panda/jsx";
import { memo, useMemo } from "react";

import type { CosmosButtonProps } from "@components/elements";
import {
	ButtonList,
	CosmosAsset,
	CosmosCardLink,
	CosmosTag,
	CosmosText,
	LinkList,
	TitleDescStack,
} from "@components/elements";
import type { AnalyticEventProps } from "@components/types";
import type { CosmosButtonFragment, CosmosModButtonFragment, FeaturePanelFragment } from "@cosmos/hygraph-schema";

const linkTagWrapStyle = css({
	display: "flex",
	flexWrap: "wrap",
	gap: "eds.dimension.12",
	minHeight: "eds.dimension.32",
	zIndex: 10,
});

const linkTagIconStyle = css({
	"& svg": {
		height: "eds.dimension.12!",
		width: "eds.dimension.12!",
	},
});

const titleDescLinksStyles = css({
	"& .link-list": {
		fontSize: "eds.font.14",
		"& .cosmos-text": {
			fontSize: "eds.font.14",
		},
		"& svg": {
			height: "eds.dimension.20!",
			width: "eds.dimension.20!",
		},
	},
	lg: { gap: "eds.dimension.24" },
});

const titleDescStyles = css({
	smDown: {
		gap: "eds.dimension.8",
		"& .cosmos-text.cosmos-title": {
			textStyle: "headingMD",
		},
		"& .cosmos-text.cosmos-desc": {
			textStyle: "paragraphSM",
		},
	},
});

const buttonListStyle = css({ zIndex: 10 });

const cardLinkStyle = css({
	zIndex: 2,
	margin: { base: "-eds.dimension.16", smDown: "-eds.dimension.8" },
});

const imageContainerRecipe = cva({
	base: {
		order: -1,
		lg: { order: -1 },
		sm: { flexBasis: "1/2" },
		borderRadius: "eds.borderRadius.8",
		overflow: "hidden",
	},
	variants: {
		disableCardBg: {
			true: {
				overflow: "visible",
				sm: { flexBasis: "3/4" },
				borderRadius: { base: "eds.borderRadius.16", smDown: "eds.borderRadius.8" },
			},
		},
		flip: {
			true: {
				lg: { order: 1 },
			},
		},
	},
});

const assetRecipe = cva({
	base: {
		objectFit: "cover",
		h: "full",
		w: "full",
		position: "relative",
		zIndex: 2,
		borderRadius: "eds.borderRadius.8",
	},
	variants: {
		disableCardBg: {
			true: {
				borderRadius: { base: "eds.borderRadius.24", smDown: "eds.borderRadius.16" },
			},
		},
	},
});

const assetWrapperFullRelativeStyle = css({ position: "relative", width: "full", height: "full" });

const assetWrapperStyle = css({
	aspectRatio: "16 / 9",
	"& .cosmos-attribution": {
		zIndex: 2,
	},
	transition: "transform token(animations.eds.duration.200) token(animations.eds.easing.easeInOutQuad)",
});

const contentContainerRecipe = cva({
	base: {
		flexBasis: "1/2",
		flexGrow: "1",
		minWidth: "0",
		width: "full",
		padding: "eds.dimension.32",
		mdDown: { padding: "eds.dimension.16" },
		smDown: { padding: "0" },
	},
	variants: {
		disableCardBg: {
			true: {
				padding: "0",
				mdDown: { padding: "0" },
			},
		},
		verticallyCenter: {
			true: {
				alignSelf: "center",
			},
		},
	},
});

const cardContainerRecipe = cva({
	base: {
		flexDir: "row",
		width: "100%",
		padding: "eds.dimension.16",
		gap: "eds.dimension.16",
		mdDown: { flexDir: "column", gap: "eds.dimension.16" },
		smDown: { gap: "eds.dimension.16" },
		backgroundColor: "eds.palette.transparent.light.005",
		backdropFilter: "var(--effect-material-blur, blur(50px))",
		borderRadius: "eds.borderRadius.24",
		border: "token(spacing.eds.dimension.1) solid token(colors.eds.palette.transparent.light.005)",
	},
	variants: {
		disableCardBg: {
			true: {
				padding: "0",
				gap: "eds.dimension.48",
				mdDown: { gap: "eds.dimension.32" },
				smDown: { gap: "eds.dimension.24" },
				backgroundColor: "",
				backdropFilter: "",
				borderRadius: "",
				border: "",
			},
		},
	},
});

const cardInteractionStyles = css({
	"&:has(.cosmos-card__link[href]:hover), &:has(.cosmos-card__link[href]:focus-visible)": {
		"& .feature-panel-asset-wrapper": {
			transform: "scale(1.05)",
		},
	},
	"&:has(.cosmos-card__link[href]:hover)": {
		backgroundColor: "eds.palette.transparent.light.010",
	},
	transition: "background token(animations.eds.duration.200) token(animations.eds.easing.easeInOutQuad)",
	"& .link-list": {
		zIndex: 10,
	},
	"& .cosmos-video-embed": {
		borderRadius: "eds.borderRadius.8",
	},
});

type LinksSectionProps = {
	links: FeaturePanelFragment["links"];
	linksAsTags?: boolean | null;
};

const LinksSection = memo(({ links, linksAsTags }: LinksSectionProps): React.JSX.Element | null => {
	if (!links?.length) return null;

	return linksAsTags ? (
		<Box className={linkTagWrapStyle}>
			{links.map((link) => (
				<CosmosTag
					className={linkTagIconStyle}
					key={link.label}
					label={link.label}
					href={link.href || ""}
					icon={link.href ? "ChevronRight" : undefined}
					iconPosition="end"
					size="uiSM"
				/>
			))}
		</Box>
	) : (
		<LinkList links={links} />
	);
});
LinksSection.displayName = "LinksSection";

const analyticsClickInfo: Partial<AnalyticEventProps> = {
	eventCategory: "button",
	interactionType: "click",
	eventAction: "navigation",
	eventValue: {
		component: "FeatureCard",
		section: "section",
	},
};

type buttonsType = "button" | "modButton";

const buttonTypesMap: Record<buttonsType, CosmosButtonProps> = {
	button: {
		size: "md",
		icon: "ArrowRightCircle",
		iconPosition: "end",
	},
	modButton: {
		size: "lg",
	},
};

const transformButtons = (
	buttons?: CosmosButtonProps[] | CosmosButtonFragment[] | CosmosModButtonFragment[],
	fallbackButtonSize?: CosmosButtonProps["size"],
	type: buttonsType = "button",
	fallbackVariant: CosmosButtonProps["variant"] = "secondary",
): CosmosButtonProps[] | undefined => {
	if (!buttons?.length) return undefined;

	const fallbackObj = buttonTypesMap[type];

	return buttons.map(
		(button: CosmosButtonProps | CosmosButtonFragment | CosmosModButtonFragment) =>
			({
				analytics: {
					...analyticsClickInfo,
					eventLabel: button.name || button.label || "",
				},
				...button,
				size: ("size" in button && button.size) || fallbackButtonSize || fallbackObj.size || undefined,
				icon: ("icon" in button && button.icon) || fallbackObj.icon || undefined,
				iconPosition: ("iconPosition" in button && button.iconPosition) || fallbackObj.iconPosition || undefined,
				variant: ("variant" in button && button.variant) || fallbackVariant,
			}) satisfies CosmosButtonProps,
	);
};

export interface FeaturePanelProps extends Omit<FeaturePanelFragment, "buttons" | "modButtons"> {
	className?: string;
	buttons?: CosmosButtonProps[] | CosmosButtonFragment[];
	modButtons?: CosmosButtonProps[] | CosmosModButtonFragment[];
	disableCardBg?: boolean | null;
	verticallyCenter?: boolean | null;
	buttonSize?: CosmosButtonProps["size"];
}

export function FeaturePanel({
	eyebrowBadges,
	eyebrow,
	title,
	gradient,
	desc,
	links,
	buttons,
	modButtons,
	asset,
	flip,
	buttonSize,
	className = "",
	linksAsTags = true,
	disableCardBg = false,
	verticallyCenter = false,
}: FeaturePanelProps): React.JSX.Element {
	const cosmosButtons = useMemo(
		() =>
			modButtons?.length
				? transformButtons(modButtons, buttonSize, "modButton")
				: transformButtons(buttons, buttonSize, "button"),
		[modButtons, buttons, buttonSize],
	);

	const cardHref = cosmosButtons?.[0]?.href;
	const shouldShowCardLink = !disableCardBg && cardHref;

	return (
		<Flex
			className={cx(
				"feature-panel",
				className,
				cardContainerRecipe({ disableCardBg: !!disableCardBg }),
				!disableCardBg && cardInteractionStyles,
			)}
		>
			<Flex
				className={cx(
					"content",
					contentContainerRecipe({ disableCardBg: !!disableCardBg, verticallyCenter: !!verticallyCenter }),
				)}
			>
				<Stack
					className={cx("title-desc-links-column", titleDescLinksStyles)}
					direction="column"
					gap="eds.dimension.16"
				>
					{eyebrowBadges?.length ? (
						<Flex gap="eds.dimension.12" flexWrap="wrap" alignItems="center">
							{eyebrowBadges.map((badge) => (
								<CosmosTag
									key={`feature-panel-eyebrow-badge-${badge.label}`}
									label={badge.label}
									href={badge.href || ""}
									size="uiSM"
								/>
							))}
						</Flex>
					) : null}

					{eyebrow ? <CosmosText variant="eyebrowMD" text={eyebrow} color="eds.text.secondary" /> : null}

					<TitleDescStack
						gradient={gradient}
						title={{ html: title?.html, text: title?.text, variant: "headingLG" }}
						description={{ html: desc, variant: "paragraphMD" }}
						stack={{ gap: "eds.dimension.16" }}
						className={titleDescStyles}
					/>

					{links?.length ? <LinksSection links={links} linksAsTags={linksAsTags} /> : null}
					{cosmosButtons?.length ? <ButtonList buttons={cosmosButtons} className={buttonListStyle} /> : null}
				</Stack>
			</Flex>

			<Flex
				alignItems="stretch"
				justifyContent="center"
				objectFit="contain"
				minWidth="0"
				className={cx(
					flip ? "image flip" : "image",
					imageContainerRecipe({ disableCardBg: !!disableCardBg, flip: !!flip }),
				)}
			>
				<Box className={assetWrapperFullRelativeStyle}>
					<Flex
						alignItems="stretch"
						justifyContent="center"
						w="full"
						h="full"
						className={cx("feature-panel-asset-wrapper", assetWrapperStyle)}
					>
						<CosmosAsset
							asset={{
								...(asset || {}),
								id: asset?.id || "",
								url: asset?.url || "",
								width: 1113,
								height: 627,
							}}
							className={assetRecipe({ disableCardBg: !!disableCardBg })}
						/>
					</Flex>
				</Box>
			</Flex>

			{shouldShowCardLink ? <CosmosCardLink href={cardHref} className={cardLinkStyle} /> : null}
		</Flex>
	);
}
